/**
 * ============================================================================
 * Supabase Client - Frontend
 * ============================================================================
 * 
 * This file provides the Supabase client for frontend authentication.
 * 
 * Status: ✅ SUPABASE-READY
 * Replaces: Manus OAuth SDK
 * 
 * Usage:
 * - signUp(email, password)
 * - signIn(email, password)
 * - signOut()
 * - getSession()
 * - onAuthStateChange(callback)
 * 
 * ============================================================================
 */

import { createClient } from '@supabase/supabase-js';
import { getRuntimeConfig } from './runtimeConfig';

const config = getRuntimeConfig();
const supabaseUrl = config.VITE_SUPABASE_URL;
const supabaseAnonKey = config.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('[Supabase] Missing environment variables (VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY)');
  console.error('[Supabase] Config loaded:', config);
}

// Create Supabase client with default localStorage
// Supabase automatically handles session persistence in localStorage
// The session is stored as: sb-{project-ref}-auth-token
export const supabase = createClient(supabaseUrl!, supabaseAnonKey!, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Use default localStorage - no custom storage needed
    // This fixes the authentication issue where custom cookie storage
    // was preventing proper session retrieval
  },
});

// Type definitions
export interface AuthUser {
  id: string;
  email: string;
  role: 'client_user' | 'mypa_data_admin' | 'mypa_super_admin';
  firstName: string | null;
  lastName: string | null;
  companyId: string | null;
  isLocked: boolean;
  language: string;
}

/**
 * Sign up with email and password
 */
export async function signUp(email: string, password: string) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
  });

  if (error) {
    throw error;
  }

  return data;
}

/**
 * Sign in with email and password
 */
export async function signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    throw error;
  }

  return data;
}

/**
 * Sign out
 */
export async function signOut() {
  const { error } = await supabase.auth.signOut();

  if (error) {
    throw error;
  }
}

/**
 * Get current session
 */
export async function getSession() {
  const { data, error } = await supabase.auth.getSession();

  if (error) {
    throw error;
  }

  return data.session;
}

/**
 * Get current user
 */
export async function getUser() {
  const { data, error } = await supabase.auth.getUser();

  if (error) {
    throw error;
  }

  return data.user;
}

/**
 * Listen to auth state changes
 */
export function onAuthStateChange(callback: (event: string, session: any) => void) {
  return supabase.auth.onAuthStateChange(callback);
}

/**
 * Get access token for API requests
 */
export async function getAccessToken(): Promise<string | null> {
  const session = await getSession();
  return session?.access_token || null;
}

